import React, { useState } from 'react';

// ─── Helper: copy text to clipboard with visual feedback ───────────────────
function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <button onClick={handleCopy} style={styles.copyBtn}>
            {copied ? '✅ Copied!' : '📋 Copy'}
        </button>
    );
}

// ─── Helper: collapsible code block ────────────────────────────────────────
function CodeBlock({ title, language = 'js', code, badge, badgeColor = '#38a169' }) {
    const [open, setOpen] = useState(false);
    return (
        <div style={styles.codeCard}>
            <div style={styles.codeCardHeader} onClick={() => setOpen(o => !o)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ ...styles.badge, background: badgeColor }}>{badge}</span>
                    <span style={styles.codeTitle}>{title}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {open && <CopyButton text={code} />}
                    <span style={styles.chevron}>{open ? '▲' : '▼'}</span>
                </div>
            </div>
            {open && (
                <pre style={styles.pre}><code>{code}</code></pre>
            )}
        </div>
    );
}

// ─── Endpoint card ──────────────────────────────────────────────────────────
function EndpointCard({ method, path, module: mod, description, requestBody, response, companyId }) {
    const isDev = process.env.NODE_ENV === 'development';
    const apiHost = isDev ? `${window.location.protocol}//${window.location.hostname}:5001` : window.location.origin;
    const baseUrl = `${apiHost}/api/webhooks`;

    const curlCode = method === 'GET'
        ? `curl -X GET "${baseUrl}${path}" \\
  -H "x-company-id: ${companyId}" \\
  -H "x-company-password: YOUR_PASSWORD"`
        : `curl -X ${method} "${baseUrl}${path}" \\
  -H "Content-Type: application/json" \\
  -H "x-company-id: ${companyId}" \\
  -H "x-company-password: YOUR_PASSWORD" \\
  -d '${JSON.stringify(requestBody, null, 2)}'`;

    const jsCode = method === 'GET'
        ? `const response = await fetch("${baseUrl}${path}", {
  method: "GET",
  headers: {
    "x-company-id": "${companyId}",
    "x-company-password": "YOUR_PASSWORD"
  }
});
const data = await response.json();
console.log(data);`
        : `const response = await fetch("${baseUrl}${path}", {
  method: "${method}",
  headers: {
    "Content-Type": "application/json",
    "x-company-id": "${companyId}",
    "x-company-password": "YOUR_PASSWORD"
  },
  body: JSON.stringify(${JSON.stringify(requestBody, null, 4)})
});
const data = await response.json();
console.log(data);`;

    const methodColor = method === 'GET' ? '#3182ce' : method === 'POST' ? '#38a169' : '#dd6b20';

    return (
        <div style={styles.endpointCard}>
            <div style={styles.endpointHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ ...styles.methodBadge, background: methodColor }}>{method}</span>
                    <code style={styles.endpointPath}>/api/webhooks{path}</code>
                    <span style={styles.modulePill}>🔒 {mod}</span>
                </div>
                <p style={styles.endpointDesc}>{description}</p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                <CodeBlock title="cURL Example" badge="cURL" badgeColor="#744210" code={curlCode} />
                <CodeBlock title="JavaScript (Fetch) Example" badge="JS" badgeColor="#b7791f" code={jsCode} />
                {response && (
                    <CodeBlock
                        title="Example Response"
                        badge="JSON"
                        badgeColor="#2b6cb0"
                        code={JSON.stringify(response, null, 2)}
                    />
                )}
            </div>
        </div>
    );
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function WebhookDocs({ onBack, companySlug, companyName, companyModules }) {
    const companyId = companySlug || 'your-company-slug';
    const activeModules = companyModules || [];

    const hasModule = (mod) => activeModules.includes(mod);

    const isDev = process.env.NODE_ENV === 'development';
    const apiHost = isDev ? `${window.location.protocol}//${window.location.hostname}:5001` : window.location.origin;
    const baseUrl = `${apiHost}/api/webhooks`;

    const ENDPOINTS = [
        {
            method: 'POST',
            path: '/address-validation',
            module: 'address-validation',
            description: 'Validate a shipping address against the UPS address database.',
            requestBody: {
                address1: '450 7th Ave',
                city: 'New York',
                state: 'NY',
                postalCode: '10123',
                country: 'US'
            },
            response: { success: true, isValid: true }
        },
        {
            method: 'POST',
            path: '/search-zone',
            module: 'search-zone',
            description: 'Look up UPS zones and check for remote area surcharges for a destination.',
            requestBody: {
                city: 'Rawalpindi',
                state: 'Punjab',
                postalCode: '46000',
                country: 'PK'
            },
            response: {
                success: true, isRemote: false, found190: false,
                charge190: '0.00', totalSurcharge: '0.00', currency: 'USD',
                details: [], message: 'no remote area or extended area'
            }
        },
        {
            method: 'POST',
            path: '/rating',
            module: 'rating',
            description: 'Get a full shipping rate including fuel surcharge, GST, and DDP duties. Supports address type indicators ("type": "01" for business, or "02" for consumer/residential inside origin and destination).',
            requestBody: {
                account: 'YOUR_UPS_ACCOUNT',
                product: 'NON-DOCUMENTS',
                origin: { city: 'Lahore', state: 'Punjab', postalCode: '54000', country: 'PK', type: '01', address2: 'Suite 100', address3: 'Floor 2', email: 'shipper@example.com', taxId: 'GB123456789', taxIdType: 'VAT' }, // 01 = Business, 02 = Consumer
                destination: { city: 'New York', state: 'NY', postalCode: '10001', country: 'US', type: '02', address2: 'Apt 4B', address3: 'Building C', email: 'consignee@example.com', taxId: 'US987654321', taxIdType: 'EIN' }, // 01 = Business, 02 = Consumer
                packages: [{ weight: 5, weightUnit: 'KGS', dimUnit: 'CM', length: 30, width: 20, height: 15 }],
                isDDP: true,
                products: [{ description: 'Cotton Shirts', hsCode: '6109.10', value: 120, quantity: 10, weight: 4.5, unitOfMeasurement: 'PCS', countryOfOrigin: 'PK', madeOf: '100% Cotton' }]
            },
            response: { success: true, rates: [{ service: 'UPS Saver (65)', totalCharge: '185.00', currency: 'USD' }] }
        },
        {
            method: 'POST',
            path: '/shipping/create',
            module: 'shipping',
            description: 'Create an export shipment. Returns tracking number, base64 label (.gif) and base64 commercial invoice PDF. Supports address type indicators ("type": "01" for business, or "02" for consumer/residential inside shipper, origin, and consignee).',
            requestBody: {
                accountNumber: 'YOUR_UPS_ACCOUNT',
                service: 'UPS Saver (65)',
                productType: 'NON-DOCUMENTS',
                isDocumentNoCommercial: false, // Set to true for Document shipments
                isDDP: true,                   // Set to true for Delivery Duty Paid
                ddpTransportationPayer: 'BillShipper',
                ddpDutyTaxPayer: 'BillThirdParty', // Or "BillShipper" / "BillReceiver"
                useBillThirdParty: true,
                tprAccount: '98765Y',
                tprAddress: { country: 'US', postalCode: '10001' },
                iossNumber: 'IMDEU1234567',
                exportReason: 'Sale',
                countryOfOrigin: 'PK',
                currencyType: 'USD',
                shipper: { name: companyName || 'Your Company', attention: 'Ali Khan', phone: '03001234567', address1: 'Main Boulevard', address2: 'Suite 100', address3: 'Floor 2', city: 'Lahore', state: 'Punjab', postalCode: '54000', country: 'PK', type: '01', email: 'shipper@example.com', taxId: 'GB123456789', taxIdType: 'VAT' }, // 01 = Business, 02 = Consumer
                origin: { name: companyName || 'Your Company', attention: 'Ali Khan', phone: '03001234567', address1: 'Main Boulevard', address2: 'Suite 100', address3: 'Floor 2', city: 'Lahore', state: 'Punjab', postalCode: '54000', country: 'PK', type: '01', email: 'origin@example.com', taxId: 'GB123456789', taxIdType: 'VAT' },
                consignee: { company: 'US Buyer Inc', name: 'John Doe', attention: 'John Doe', phone: '2125550199', address1: '450 7th Ave', address2: 'Apt 4B', address3: 'Building C', city: 'New York', state: 'NY', postalCode: '10123', country: 'US', type: '02', email: 'consignee@example.com', taxId: 'US987654321', taxIdType: 'EIN' }, // 01 = Business, 02 = Consumer
                packages: [{ weight: 10, weightUnit: 'KGS', dimUnit: 'CM', length: 40, width: 30, height: 25 }],
                products: [{ description: 'Cotton Shirts', hsCode: '6109.10', value: 20, quantity: 15, weight: 9.5, unitOfMeasurement: 'PCS', countryOfOrigin: 'PK', madeOf: '100% Cotton' }]
            },
            response: { success: true, trackingNumber: '1Z12345E0205271688', labelImage: '<base64 .gif string>', upsCommercialInvoice: '<base64 PDF string>' }
        },
        {
            method: 'POST',
            path: '/fc-label/get-rate',
            module: 'fc-label',
            description: 'Get the import rate (FC Label) for a parcel coming from overseas.',
            requestBody: {
                accountNumber: 'YOUR_UPS_ACCOUNT',
                product: 'NON-DOCUMENTS',
                originCountry: 'US',
                totalWeightKg: 12.5,
                originPostalCode: '90001',
                originCity: 'Los Angeles',
                originState: 'CA'
            },
            response: { success: true, rate: '95.00', currency: 'USD' }
        },
        {
            method: 'POST',
            path: '/fc-label/create',
            module: 'fc-label',
            description: 'Create an import control label (FC Label). Returns tracking number and base64 label.',
            requestBody: {
                accountNumber: 'YOUR_UPS_ACCOUNT',
                serviceType: 'NON-DOCUMENTS',
                generalDescription: 'Return Merchandise',
                shipFrom: { name: 'Jane Smith', attention: 'Jane Smith', phone: '3105550144', address1: '789 Sunset Blvd', city: 'Los Angeles', state: 'CA', postalCode: '90028', country: 'US' },
                shipTo: { name: companyName || 'Your Company', attention: 'Receiving Team', phone: '021111222333', address1: 'Korangi Industrial Area', city: 'Karachi', state: 'Sindh', postalCode: '74900', country: 'PK' },
                packages: [{ weight: 6.5, weightUnit: 'KGS', dimUnit: 'CM', length: 30, width: 20, height: 20 }]
            },
            response: { success: true, trackingNumber: '1Z12345E0205271688', labelImage: '<base64 .gif string>' }
        },
        {
            method: 'GET',
            path: '/track/1Z12345E0205271688',
            module: 'tracking',
            description: 'Track a shipment by tracking number. Returns live delivery status from UPS.',
            requestBody: null,
            response: { success: true, status: 'In Transit', activity: [{ date: '2024-01-10', description: 'Package in transit' }] }
        },
        {
            method: 'GET',
            path: '/billing/list?page=1&limit=10',
            module: 'billing',
            description: 'Get a paginated list of all shipments and billing records for your company.',
            requestBody: null,
            response: { success: true, totalCount: 42, billings: [{ trackingNumber: '1Z...', totalCharges: '185.00', currency: 'USD', createdAt: '2024-01-10' }] }
        },
        {
            method: 'GET',
            path: '/shipment/1Z12345E0205271688',
            module: 'shipping',
            description: 'Fetch a single shipment record including its base64 label and commercial invoice.',
            requestBody: null,
            response: { success: true, shipment: { trackingNumber: '1Z...', totalCharges: '185.00', labelImage: '<base64>', upsCommercialInvoice: '<base64 PDF>' } }
        }
    ];

    const visibleEndpoints = ENDPOINTS.filter(ep => hasModule(ep.module));

    return (
        <div style={styles.container}>
            {/* Back Button */}
            <button onClick={onBack} style={styles.backBtn}>← Back to Dashboard</button>

            {/* Header */}
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>🔌 Webhook API Documentation</h1>
                    <p style={styles.subtitle}>
                        Integrate your systems with <strong>{companyName || 'Your Company'}</strong> via REST API.
                        Your credentials are pre-filled in every example below.
                    </p>
                </div>
            </div>

            {/* Auth Card */}
            <div style={styles.authCard}>
                <h3 style={styles.sectionTitle}>🔐 Authentication</h3>
                <p style={styles.authDesc}>
                    All requests require these two HTTP headers. Your <strong>Company ID</strong> is fixed.
                    Your <strong>password</strong> is the password you use to log into this portal.
                </p>
                <div style={styles.credRow}>
                    <div style={styles.credItem}>
                        <label style={styles.credLabel}>x-company-id (Header)</label>
                        <div style={styles.credValue}>
                            <code style={styles.credCode}>{companyId}</code>
                            <CopyButton text={companyId} />
                        </div>
                    </div>
                    <div style={styles.credItem}>
                        <label style={styles.credLabel}>x-company-password (Header)</label>
                        <div style={styles.credValue}>
                            <code style={styles.credCode}>Your login password</code>
                        </div>
                    </div>
                </div>
                <div style={styles.baseUrlRow}>
                    <label style={styles.credLabel}>Base URL</label>
                    <div style={styles.credValue}>
                        <code style={styles.credCode}>
                            {baseUrl}
                        </code>
                        <CopyButton text={baseUrl} />
                    </div>
                </div>
            </div>

            {/* ── Authentication Code Guide ── */}
            <div style={styles.authGuideSection}>
                <h3 style={styles.sectionTitle}>🛡️ Authentication Code Guide</h3>
                <p style={styles.authDesc}>
                    Every request must include <code style={styles.inlineCode}>x-company-id</code> and <code style={styles.inlineCode}>x-company-password</code> headers.
                    Below is the server-side flow and ready-to-use code in multiple languages.
                </p>

                {/* Server Flow */}
                <div style={styles.flowBox}>
                    <strong>Server Authentication Flow:</strong>
                    <ol style={styles.flowList}>
                        <li>Server reads <code style={styles.inlineCode}>x-company-id</code> → looks up company by slug or ObjectId</li>
                        <li>Fetches owner user from <code style={styles.inlineCode}>company_{'<slug>'}_users</code> collection</li>
                        <li>Runs <code style={styles.inlineCode}>bcrypt.compare(sentPassword, storedHash)</code></li>
                        <li>If wrong → <strong style={{ color: '#e53e3e' }}>HTTP 401</strong> Unauthorized</li>
                        <li>Checks if requested endpoint's module is in <code style={styles.inlineCode}>company.modules[]</code></li>
                        <li>If module disabled → <strong style={{ color: '#dd6b20' }}>HTTP 403</strong> Forbidden</li>
                        <li>Otherwise → calls UPS / DB and returns JSON ✅</li>
                    </ol>
                </div>

                {/* Error Reference */}
                <div style={styles.errorRef}>
                    <strong>Error Responses:</strong>
                    <table style={styles.errorTable}>
                        <thead>
                            <tr>
                                <th style={styles.th}>HTTP Code</th>
                                <th style={styles.th}>Meaning</th>
                                <th style={styles.th}>Fix</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr><td style={styles.td}><code style={{ ...styles.inlineCode, color: '#e53e3e' }}>401</code></td><td style={styles.td}>Wrong company ID or password</td><td style={styles.td}>Check your <code style={styles.inlineCode}>x-company-id</code> and <code style={styles.inlineCode}>x-company-password</code> headers</td></tr>
                            <tr><td style={styles.td}><code style={{ ...styles.inlineCode, color: '#dd6b20' }}>403</code></td><td style={styles.td}>Module not enabled</td><td style={styles.td}>Ask admin to enable the required module</td></tr>
                            <tr><td style={styles.td}><code style={{ ...styles.inlineCode, color: '#d69e2e' }}>400</code></td><td style={styles.td}>Missing/invalid request fields</td><td style={styles.td}>Check request body against the endpoint docs below</td></tr>
                            <tr><td style={styles.td}><code style={{ ...styles.inlineCode, color: '#718096' }}>500</code></td><td style={styles.td}>Server / UPS API error</td><td style={styles.td}>Retry or contact support</td></tr>
                        </tbody>
                    </table>
                </div>

                {/* Code Examples */}
                <CodeBlock
                    title="Node.js — using native fetch"
                    badge="Node.js"
                    badgeColor="#68d391"
                    code={`// Node.js 18+ (native fetch)
const response = await fetch('${apiHost}/api/webhooks/address-validation', {
    method: 'POST',
    headers: {
        'Content-Type':       'application/json',
        'x-company-id':       '${companyId}',
        'x-company-password': 'YOUR_PASSWORD'
    },
    body: JSON.stringify({
        address1: '450 7th Ave',
        city:     'New York',
        state:    'NY',
        postalCode: '10123',
        country:  'US'
    })
});

if (response.status === 401) {
    console.error('Wrong credentials!');
} else if (response.status === 403) {
    console.error('Module not enabled for your company.');
} else {
    const data = await response.json();
    console.log(data);
}`}
                />

                <CodeBlock
                    title="Node.js — using Axios (recommended)"
                    badge="Axios"
                    badgeColor="#5a67d8"
                    code={`const axios = require('axios');

const apiClient = axios.create({
    baseURL: '${apiHost}/api/webhooks',
    headers: {
        'Content-Type':       'application/json',
        'x-company-id':       '${companyId}',
        'x-company-password': 'YOUR_PASSWORD'
    },
    timeout: 30000
});

// Usage — all requests auto-include auth headers
try {
    const { data } = await apiClient.post('/address-validation', {
        address1: '450 7th Ave', city: 'New York',
        state: 'NY', postalCode: '10123', country: 'US'
    });
    console.log(data);
} catch (err) {
    if (err.response?.status === 401) console.error('Wrong credentials');
    if (err.response?.status === 403) console.error('Module not enabled');
}`}
                />

                <CodeBlock
                    title="Python — using requests"
                    badge="Python"
                    badgeColor="#f6e05e"
                    code={`import requests

BASE_URL = "${apiHost}/api/webhooks"

headers = {
    "Content-Type":       "application/json",
    "x-company-id":       "${companyId}",
    "x-company-password": "YOUR_PASSWORD"
}

# Address Validation
resp = requests.post(f"{BASE_URL}/address-validation", json={
    "address1": "450 7th Ave", "city": "New York",
    "state": "NY", "postalCode": "10123", "country": "US"
}, headers=headers)

if resp.status_code == 401:
    print("Wrong credentials!")
elif resp.status_code == 403:
    print("Module not enabled for your company.")
else:
    print(resp.json())

# Create a Shipment & save label
resp = requests.post(f"{BASE_URL}/shipping/create", json={...}, headers=headers)
data = resp.json()

import base64
with open(f"label_{data['trackingNumber']}.gif", "wb") as f:
    f.write(base64.b64decode(data["labelImage"]))

if data.get("upsCommercialInvoice"):
    with open(f"invoice_{data['trackingNumber']}.pdf", "wb") as f:
        f.write(base64.b64decode(data["upsCommercialInvoice"]))`}
                />

                <CodeBlock
                    title="PHP — using cURL"
                    badge="PHP"
                    badgeColor="#8b5cf6"
                    code={`<?php
$baseUrl = "${apiHost}/api/webhooks";
$headers = [
    "Content-Type: application/json",
    "x-company-id: ${companyId}",
    "x-company-password: YOUR_PASSWORD"
];

$payload = json_encode([
    "address1"   => "450 7th Ave",
    "city"       => "New York",
    "state"      => "NY",
    "postalCode" => "10123",
    "country"    => "US"
]);

$ch = curl_init("$baseUrl/address-validation");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, $payload);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_TIMEOUT, 30);

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($httpCode === 401) { echo "Wrong credentials!\\n"; }
elseif ($httpCode === 403) { echo "Module not enabled!\\n"; }
else { print_r(json_decode($response, true)); }
?>`}
                />

                <CodeBlock
                    title="cURL — Terminal / Shell"
                    badge="cURL"
                    badgeColor="#744210"
                    code={`# Address Validation
curl -X POST "${apiHost}/api/webhooks/address-validation" \\
  -H "Content-Type: application/json" \\
  -H "x-company-id: ${companyId}" \\
  -H "x-company-password: YOUR_PASSWORD" \\
  -d '{"address1":"450 7th Ave","city":"New York","state":"NY","postalCode":"10123","country":"US"}'

# Track a shipment
curl -X GET "${apiHost}/api/webhooks/track/1Z12345E0205271688" \\
  -H "x-company-id: ${companyId}" \\
  -H "x-company-password: YOUR_PASSWORD"

# List billing records
curl -X GET "${apiHost}/api/webhooks/billing/list?page=1&limit=10" \\
  -H "x-company-id: ${companyId}" \\
  -H "x-company-password: YOUR_PASSWORD"`}
                />
            </div>

            {/* Module Access Info */}
            <div style={styles.moduleBar}>
                <span style={styles.moduleBarLabel}>Your Active Modules:</span>
                {activeModules.filter(m => !['settings', 'user-access'].includes(m)).map(m => (
                    <span key={m} style={styles.modulePill}>✅ {m}</span>
                ))}
            </div>

            {/* Endpoints */}
            <h3 style={{ ...styles.sectionTitle, marginTop: '24px' }}>📡 Available Endpoints ({visibleEndpoints.length})</h3>
            {visibleEndpoints.length === 0 ? (
                <div style={styles.noModules}>
                    No API endpoints are available for your current modules.
                    Contact your administrator to enable additional modules.
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {visibleEndpoints.map((ep, i) => (
                        <EndpointCard key={i} {...ep} companyId={companyId} />
                    ))}
                </div>
            )}

            {/* Save base64 tip */}
            <div style={styles.tipBox}>
                <strong>💡 Working with Base64 Labels & Invoices</strong>
                <p style={{ margin: '8px 0 0' }}>
                    The <code>labelImage</code> field is a base64-encoded <strong>.gif</strong> file (the shipping label).
                    The <code>upsCommercialInvoice</code> field is a base64-encoded <strong>.pdf</strong>.
                    To save them in Node.js:
                </p>
                <pre style={{ ...styles.pre, marginTop: '10px' }}>{`const fs = require('fs');

// Save label as .gif
const labelBuffer = Buffer.from(data.labelImage, 'base64');
fs.writeFileSync('label_' + data.trackingNumber + '.gif', labelBuffer);

// Save commercial invoice as .pdf
const invoiceBuffer = Buffer.from(data.upsCommercialInvoice, 'base64');
fs.writeFileSync('invoice_' + data.trackingNumber + '.pdf', invoiceBuffer);`}</pre>
                <p style={{ margin: '10px 0 0', fontSize: '0.85rem', color: '#718096' }}>
                    In a browser, you can open them using: <code>{'window.open("data:application/pdf;base64," + data.upsCommercialInvoice)'}</code>
                </p>
            </div>
        </div>
    );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
    container: {
        maxWidth: '960px',
        margin: '0 auto',
        padding: '24px 20px 60px',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        color: '#1a202c',
    },
    backBtn: {
        background: 'none',
        border: 'none',
        color: '#3182ce',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: 600,
        padding: '0 0 16px',
        display: 'block',
    },
    header: {
        background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
        color: 'white',
        borderRadius: '16px',
        padding: '32px',
        marginBottom: '24px',
    },
    title: {
        margin: 0,
        fontSize: '1.8rem',
        fontWeight: 800,
        letterSpacing: '-0.5px',
    },
    subtitle: {
        margin: '10px 0 0',
        opacity: 0.8,
        fontSize: '0.95rem',
        lineHeight: '1.5',
    },
    authCard: {
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '16px',
        boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
    },
    sectionTitle: {
        margin: '0 0 14px',
        fontSize: '1.05rem',
        fontWeight: 700,
        color: '#2d3748',
    },
    authDesc: {
        color: '#718096',
        fontSize: '0.9rem',
        margin: '0 0 16px',
        lineHeight: '1.5',
    },
    credRow: {
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        marginBottom: '12px',
    },
    credItem: {
        flex: '1 1 250px',
    },
    credLabel: {
        display: 'block',
        fontSize: '0.75rem',
        fontWeight: 700,
        color: '#718096',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '6px',
    },
    credValue: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: '#f7fafc',
        borderRadius: '8px',
        padding: '10px 14px',
        border: '1px solid #e2e8f0',
    },
    credCode: {
        flex: 1,
        fontFamily: 'monospace',
        fontSize: '0.9rem',
        color: '#2d3748',
        wordBreak: 'break-all',
    },
    baseUrlRow: {
        marginTop: '4px',
    },
    moduleBar: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px',
        background: '#ebf8ff',
        borderRadius: '10px',
        padding: '12px 16px',
        border: '1px solid #bee3f8',
        marginBottom: '8px',
    },
    moduleBarLabel: {
        fontWeight: 700,
        fontSize: '0.85rem',
        color: '#2b6cb0',
    },
    modulePill: {
        background: '#c6f6d5',
        color: '#276749',
        borderRadius: '20px',
        padding: '3px 10px',
        fontSize: '0.78rem',
        fontWeight: 600,
    },
    endpointCard: {
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '14px',
        padding: '20px',
        boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
    },
    endpointHeader: {
        borderBottom: '1px solid #f0f0f0',
        paddingBottom: '12px',
        marginBottom: '4px',
    },
    methodBadge: {
        color: 'white',
        fontWeight: 800,
        borderRadius: '6px',
        padding: '3px 10px',
        fontSize: '0.75rem',
        fontFamily: 'monospace',
        letterSpacing: '0.5px',
    },
    endpointPath: {
        fontFamily: 'monospace',
        fontSize: '0.9rem',
        color: '#2d3748',
        background: '#f7fafc',
        borderRadius: '6px',
        padding: '3px 8px',
        border: '1px solid #e2e8f0',
    },
    endpointDesc: {
        margin: '10px 0 0',
        color: '#718096',
        fontSize: '0.88rem',
        lineHeight: '1.5',
    },
    codeCard: {
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        overflow: 'hidden',
    },
    codeCardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        background: '#f7fafc',
        cursor: 'pointer',
        userSelect: 'none',
    },
    codeTitle: {
        fontWeight: 600,
        fontSize: '0.85rem',
        color: '#4a5568',
    },
    badge: {
        color: 'white',
        borderRadius: '5px',
        padding: '2px 8px',
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.5px',
        fontFamily: 'monospace',
    },
    chevron: {
        fontSize: '0.7rem',
        color: '#718096',
    },
    pre: {
        margin: 0,
        background: '#1a202c',
        color: '#e2e8f0',
        padding: '16px',
        fontSize: '0.8rem',
        lineHeight: '1.6',
        overflowX: 'auto',
        fontFamily: "'Fira Code', 'Cascadia Code', monospace",
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
    },
    copyBtn: {
        background: '#2d3748',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        padding: '4px 10px',
        fontSize: '0.75rem',
        cursor: 'pointer',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    tipBox: {
        background: '#fffaf0',
        border: '1px solid #fbd38d',
        borderRadius: '14px',
        padding: '20px',
        marginTop: '24px',
        fontSize: '0.88rem',
        lineHeight: '1.6',
        color: '#744210',
    },
    noModules: {
        background: '#fff5f5',
        border: '1px solid #fed7d7',
        borderRadius: '10px',
        padding: '20px',
        color: '#c53030',
        textAlign: 'center',
    },
    // ── Auth Guide Section Styles ──
    authGuideSection: {
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '16px',
        boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
    },
    inlineCode: {
        background: '#edf2f7',
        borderRadius: '4px',
        padding: '2px 6px',
        fontFamily: 'monospace',
        fontSize: '0.85em',
        color: '#2d3748',
    },
    flowBox: {
        background: '#f0fff4',
        border: '1px solid #c6f6d5',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '16px',
        fontSize: '0.88rem',
        lineHeight: '1.7',
        color: '#22543d',
    },
    flowList: {
        margin: '10px 0 0',
        paddingLeft: '20px',
    },
    errorRef: {
        background: '#fff5f5',
        border: '1px solid #fed7d7',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '16px',
        fontSize: '0.88rem',
    },
    errorTable: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: '10px',
    },
    th: {
        textAlign: 'left',
        padding: '8px 10px',
        borderBottom: '2px solid #e2e8f0',
        fontSize: '0.8rem',
        fontWeight: 700,
        color: '#4a5568',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    td: {
        padding: '8px 10px',
        borderBottom: '1px solid #f0f0f0',
        fontSize: '0.85rem',
        color: '#2d3748',
        verticalAlign: 'top',
    },
};
